import { type Point, type PortLayout, PortIndex, toPoint } from '../../types/diagram.tsx';
import { add, distance, multiply, subtract } from '../2d/index.tsx';
import { fireError } from '../fire-errors/index.tsx';
import { type Vector, Axis, Direction, axisOf, extractPoint } from './types.tsx';

export const CONNECTION_ENDS_PADDING = 20;

/**
 * Returns the point of intersection between two vectors or `undefined` if 1. they never meet or 2. they are intersect on infinitely (i.e. on the same line)
 */
export const intersection = (a: Vector, b: Vector): Point | undefined => {
	if (coincides(a, b)) return extractPoint(a);

	if (a.direction === Direction.up || a.direction === Direction.down) {
		return toPoint(a.x, b.y);
	}

	if (b.direction === Direction.up || b.direction === Direction.down) {
		return toPoint(b.x, a.y);
	}

	return undefined;
};

/**
 * Moves a vector an given amount on the direction it is pointing
 */
export const displace = (vector: Vector, amount: number): Vector => {
	switch (vector.direction) {
		case Direction.up:
			return { ...vector, y: vector.y - amount };
		case Direction.down:
			return { ...vector, y: vector.y + amount };
		case Direction.left:
			return { ...vector, x: vector.x - amount };
		case Direction.right:
			return { ...vector, x: vector.x + amount };
		default:
			return vector;
	}
};

/**
 * Readability helper to modify `original` vector's properties mutatively with new provided properties
 */
export const mutate = (original: Vector, { direction, x, y }: Vector): void => {
	// eslint-disable-next-line no-param-reassign
	original.direction = direction;
	// eslint-disable-next-line no-param-reassign
	original.x = x;
	// eslint-disable-next-line no-param-reassign
	original.y = y;
};

export const commonAxis = (vector: Vector, other: Vector): Axis | undefined => {
	const vectorAxis = axisOf(vector);
	const otherAxis = axisOf(other);

	if (vectorAxis === Axis.y && otherAxis === Axis.y) {
		return Axis.y;
	}

	if (vectorAxis === Axis.x && otherAxis === Axis.x) {
		return Axis.x;
	}

	// perpendicular
	return undefined;
};

export const portIndexToVector = ({ portIndex, position: { x, y } }: PortLayout): Vector => {
	switch (portIndex) {
		case PortIndex.TopLeft:
		case PortIndex.Top:
		case PortIndex.TopRight:
			return { x, y, direction: Direction.up };
		case PortIndex.BottomLeft:
		case PortIndex.Bottom:
		case PortIndex.BottomRight:
			return { x, y, direction: Direction.down };
		case PortIndex.Right:
			return { x, y, direction: Direction.right };
		default: // left
			return { x, y, direction: Direction.left };
	}
};

export const inFrontOf = (vector: Vector, other: Point): boolean => {
	switch (vector.direction) {
		case Direction.up:
			return other.y < vector.y;
		case Direction.down:
			return other.y > vector.y;
		case Direction.left:
			return other.x < vector.x;
		case Direction.right:
			return other.x > vector.x;
		default:
			return false;
	}
};

/** Are the two points on the same position */
export const coincides = (point: Point, other: Point) => point.x === other.x && point.y === other.y;

/** Determines the Direction a vector needs to point to face a point */
export const turnDirection = (vector: Vector, point: Point): Direction | undefined => {
	const dx = point.x - vector.x;
	const dy = point.y - vector.y;

	if (dx === 0 && dy === 0) {
		return undefined;
	}

	if (axisOf(vector) === Axis.y) {
		if (dx < 0) return Direction.left;
		if (dx > 0) return Direction.right;
		if (dy > 0) return Direction.down;
		if (dy < 0) return Direction.up;

		// must be in front, don't turn
		return vector.direction;
	}

	if (axisOf(vector) === Axis.x) {
		if (dy < 0) return Direction.up;
		if (dy > 0) return Direction.down;
		if (dx > 0) return Direction.right;
		if (dx < 0) return Direction.left;

		// must be in front, don't turn
		return vector.direction;
	}

	return undefined;
};

export const hasSharedAxis = (subject: Point, other: Point) =>
	subject.x === other.x || subject.y === other.y;

/** Calculates the total distance of a continuous path connected by the provided Points */
export const distanceAll = (...points: Point[]) => {
	const [firstPoint, ...remainingPoints] = points;

	let total = 0;
	remainingPoints.reduce((prev, curr) => {
		total += distance(prev, curr);
		return curr;
	}, firstPoint);

	return total;
};

/**
 * Provides the Point on a continous path (represented by connected Points) where
 * traveling the path from the first point onwards, we stop once we've travelled
 * the provided `stopAtDistance` value.
 */
export const pointOnPathByDistance = (points: Point[], stopAtDistance: number): Point => {
	if (points.length === 0) {
		fireError(
			'Unexepcted empty points array. Cannot determine point on path.',
			'manhattan-routing.point-on-path-by-distance',
		);
		return toPoint(0, 0);
	}

	let distanceTravelled = 0;
	for (let i = 1; i < points.length; i += 1) {
		const prev = points[i - 1];
		const curr = points[i];

		const segmentLength = distance(prev, curr);
		distanceTravelled += segmentLength;

		if (distanceTravelled >= stopAtDistance) {
			const perc = (distanceTravelled - stopAtDistance) / segmentLength;
			return add(curr, multiply(subtract(prev, curr), perc));
		}
	}

	const endPoint = points[points.length - 1];
	return endPoint;
};
